import {
  Check,
  ExternalLink,
  Loader2,
  Pencil,
  Workflow,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { TRACE_UI_URL } from '@/utils/constants';

export type WorkflowCardProps = {
  workflowId: string;
  workflowName: string;
  publishedId: string | null;
  isPrivileged: boolean;
  onSavePublishedId: (
    workflowId: string,
    publishedId: string | null
  ) => Promise<void>;
};

export const WorkflowCard = ({
  workflowId,
  workflowName,
  publishedId,
  isPrivileged,
  onSavePublishedId
}: WorkflowCardProps) => {
  const [isEditingPublishedId, setIsEditingPublishedId] = useState(false);
  const [editValue, setEditValue] = useState(publishedId ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditValue(publishedId ?? '');
  }, [publishedId]);

  const handleSave = async () => {
    const newValue = editValue.trim() || null;
    if (newValue === publishedId) {
      setIsEditingPublishedId(false);
      return;
    }
    setSaving(true);
    await onSavePublishedId(workflowId, newValue);
    setSaving(false);
    setIsEditingPublishedId(false);
  };

  const handleCancel = () => {
    setEditValue(publishedId ?? '');
    setIsEditingPublishedId(false);
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-card p-5 shadow-verail">
      {/* Workflow name + Open in Trace */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Workflow className="size-4 text-muted-foreground" />
          </div>
          <span className="text-pretty font-medium text-sm leading-tight">
            {workflowName}
          </span>
        </div>
        {publishedId && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  asChild
                  className="h-7 w-7 shrink-0 p-0"
                  size="sm"
                  variant="ghost"
                >
                  <a
                    href={`${TRACE_UI_URL}/workflow/${publishedId}`}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <ExternalLink className="size-3.5" />
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent variant="dark">Open in Trace</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Published ID */}
      <div className="flex flex-col gap-1.5">
        <span className="text-muted-foreground text-xs uppercase tracking-wide">
          Published ID
        </span>
        {isEditingPublishedId ? (
          <div className="flex h-8 items-center gap-1.5">
            <Input
              autoFocus
              className="h-8 font-mono text-xs"
              disabled={saving}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  void handleSave();
                }
                if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
              placeholder="Enter published ID..."
              value={editValue}
            />
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Save published ID"
                    className="h-7 w-7 shrink-0 p-0"
                    disabled={saving}
                    onClick={() => void handleSave()}
                    size="sm"
                    variant="ghost"
                  >
                    {saving ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <Check className="size-3" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent variant="dark">Save</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Cancel editing"
                    className="h-7 w-7 shrink-0 p-0"
                    disabled={saving}
                    onClick={handleCancel}
                    size="sm"
                    variant="ghost"
                  >
                    <X className="size-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent variant="dark">Cancel</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : (
          <div className="flex h-8 items-center justify-between gap-2">
            {publishedId ? (
              <span className="rounded-md bg-muted px-2 py-1 font-mono text-muted-foreground text-xs">
                {publishedId}
              </span>
            ) : (
              <span className="text-muted-foreground text-sm italic">
                Not published
              </span>
            )}
            {isPrivileged && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label="Edit published ID"
                      className="h-7 w-7 shrink-0 p-0"
                      onClick={() => setIsEditingPublishedId(true)}
                      size="sm"
                      variant="ghost"
                    >
                      <Pencil className="size-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent variant="dark">
                    Edit published ID
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
